import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppSelector } from '../../../../../../store/hooks'
import {
  selectMeetingSummaryAgentDisplayedPresets,
  selectMeetingSummaryAgentLoadingInteraction,
  selectMeetingSummaryAgentShouldScrollSidebar,
  selectMeetingSummaryAgentShowChat,
  selectMeetingSummaryFindAndReplaceLoading,
} from '../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { StreamingAIAgentChat } from '../../../../../../views/AIAgent/Chat/StreamingAIAgentChat'
import { MeetingSummaryInteraction, MeetingSummaryInteractionType } from '../../../../../../models/MeetingSummary'
import { selectSelectedMeetingTemplate } from '../../../../../../store/meetingSummary/meetingTemplates'
import { MeetingTemplatePresetView } from '../../../../../../views/Setup/MeetingTemplates/MeetingTemplateView'
import { ResultCard } from '../../MeetingResults/ResultCard/ResultCard'
import { useMeetingSummary } from '../hooks'

const _MEETING_SUMMARY_AGENT_CHAT_INFO_TEXT = `The AI Assistant is best used for complex or batch operations that apply to multiple sections or the whole summary. 
For editing one off typos or adding small corrections, manual editing is recommended.`

export const MeetingSummarySidebar: React.FC = () => {
  const { streamingInteractWithMeetingSummary, resubmitLastInteraction } = useMeetingSummary()
  const showChat = useAppSelector(selectMeetingSummaryAgentShowChat)
  const displayedPresets = useAppSelector(selectMeetingSummaryAgentDisplayedPresets)
  const shouldScrollSidebar = useAppSelector(selectMeetingSummaryAgentShouldScrollSidebar)

  const chatRef = useRef<HTMLDivElement | null>(null)
  const displayedPresetsRef = useRef<(HTMLDivElement | null)[]>([])

  const scrollToElement = useCallback(
    (element: HTMLDivElement) => {
      const headerOffset = 160
      if (shouldScrollSidebar) {
        window.scrollTo({
          top: element.offsetTop - headerOffset,
          behavior: 'smooth',
        })
      }
    },
    [shouldScrollSidebar],
  )

  useEffect(() => {
    if (showChat && chatRef) {
      const { current } = chatRef
      if (current) {
        scrollToElement(current)
      }
    }
  }, [scrollToElement, showChat, chatRef])

  useEffect(() => {
    const { current } = displayedPresetsRef
    if (displayedPresets.length && current && current.length) {
      const lastElement = current[current.length - 1]
      if (lastElement) {
        scrollToElement(lastElement)
      }
    }
  }, [scrollToElement, displayedPresets, displayedPresetsRef])

  return (
    <div className='d-flex flex-column gap-2'>
      {showChat ? (
        <div ref={chatRef}>
          <MeetingSummaryChat interactWithMeetingSummary={streamingInteractWithMeetingSummary} resubmitLastInteraction={resubmitLastInteraction} />
        </div>
      ) : null}
      {displayedPresets.map((aiFeaturePresetId, i) => {
        return (
          <div key={aiFeaturePresetId} ref={(el) => (displayedPresetsRef.current[i] = el)}>
            <SidebarPreset aiFeaturePresetId={aiFeaturePresetId} />
          </div>
        )
      })}
    </div>
  )
}

interface MeetingSummaryChatProps {
  interactWithMeetingSummary: (interaction: MeetingSummaryInteraction) => Promise<void>
  resubmitLastInteraction: () => Promise<void>
}

const MeetingSummaryChat: React.FC<MeetingSummaryChatProps> = ({ interactWithMeetingSummary, resubmitLastInteraction }) => {
  const loadingInteraction = useAppSelector(selectMeetingSummaryAgentLoadingInteraction)
  const findAndReplaceLoading = useAppSelector(selectMeetingSummaryFindAndReplaceLoading)

  const handleChat = async (chatMessage: string) => {
    await interactWithMeetingSummary({ type: MeetingSummaryInteractionType.CHAT, query: chatMessage })
  }

  return (
    <StreamingAIAgentChat
      isLoading={loadingInteraction === MeetingSummaryInteractionType.CHAT || findAndReplaceLoading}
      disabled={!!loadingInteraction || findAndReplaceLoading}
      handleSubmit={handleChat}
      infoText={_MEETING_SUMMARY_AGENT_CHAT_INFO_TEXT}
      className='zocks-border-color-primary border'
      handleResubmit={resubmitLastInteraction}
    />
  )
}

interface SidebarPresetProps {
  aiFeaturePresetId: string
}

const SidebarPreset: React.FC<SidebarPresetProps> = ({ aiFeaturePresetId }) => {
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  const meetingTemplatePreset = selectedMeetingTemplate?.presets.find((preset) => preset.aiFeaturePresetId === aiFeaturePresetId)
  const { aiFeatureId } = meetingTemplatePreset ?? {}

  const meetingTemplatePresetView: MeetingTemplatePresetView = useMemo(
    () => ({
      aiFeaturePresetId,
      aiFeatureId,
      row: 0,
      column: 0,
      colSpan: 1,
    }),
    [aiFeaturePresetId, aiFeatureId],
  )

  if (!meetingTemplatePreset) {
    return null
  }

  return <ResultCard meetingTemplatePresetView={meetingTemplatePresetView} />
}
